// @flow

import React, {Fragment} from 'react';
import classNames from 'classnames';
import InstantBookIcon from '../InstantBookIcon/InstantBookIcon';
import {FormattedMessage} from 'react-intl';
import DualPriceTooltipContainer from '../DualPriceTooltip/DualPriceTooltipContainer';
import type {ListingShape} from '../Hit/Hit';
import type {IntlShape} from 'react-intl';
import type {Node} from 'react';
import './DualPrice.less';
import type {PriceSummaryShape} from '../Price/Price';

export type PriceItemShape = {
    ...PriceSummaryShape,
    roundedFormattedAmount: ?string,
    isPrimary: boolean,
    isPriceBold: boolean,
    withTooltip?: boolean
};

export type PriceLabelShape = {
    priceTypeId: string,
    pricePeriodDescription: string
};

export type ListingPricesShape = {
    nightlyPrice: PriceItemShape,
    totalPrice: PriceItemShape,
    priceLabel: PriceLabelShape
};

const listingHasPriceInfo = (listing: ListingShape): boolean => {
    const {priceSummary, priceSummarySecondary, priceLabel} = listing;

    return (
        priceSummary !== null ||
        priceSummarySecondary !== null ||
        priceLabel !== null
    );
};

const getListingPrices = (
    listing: ListingShape,
    withTooltip?: boolean
): ListingPricesShape => {
    const defaultPrice = {
        priceTypeId: 'UNKNOWN',
        pricePeriodDescription: '',
        formattedAmount: '',
        roundedFormattedAmount: ''
    };
    const defaultLabel = {
        priceTypeId: 'UNKNOWN',
        pricePeriodDescription: ''
    };
    const listingPriceSummary = listing.priceSummary || defaultPrice;
    const listingPriceSummarySecondary =
        listing.priceSummarySecondary || defaultPrice;
    const listingPriceSummaryId = listingPriceSummary.priceTypeId;
    const listingPriceSummarySecondaryId =
        listingPriceSummarySecondary.priceTypeId;
    const priceLabel = listing.priceLabel || defaultLabel;
    let nightlyPrice = {};
    let totalPrice = {};

    if (
        listingPriceSummaryId === 'PER_NIGHT' ||
        listingPriceSummarySecondaryId === 'UNKNOWN'
    ) {
        nightlyPrice = {
            ...listingPriceSummary,
            isPriceBold: true,
            isPrimary: true
        };
        totalPrice = {
            ...listingPriceSummarySecondary,
            isPriceBold: false,
            isPrimary: false,
            withTooltip
        };
    } else {
        totalPrice = {
            ...listingPriceSummary,
            isPriceBold: true,
            isPrimary: true,
            withTooltip
        };
        nightlyPrice = {
            ...listingPriceSummarySecondary,
            isPriceBold: false,
            isPrimary: false
        };
    }

    return {
        nightlyPrice,
        totalPrice,
        priceLabel
    };
};

export const DualPrice = ({
    listing,
    intl,
    isDesktop = true,
    showInstantBookIcon = true,
    withTooltip = false
}: {
    listing: ListingShape,
    intl: IntlShape,
    isDesktop: boolean,
    showInstantBookIcon?: boolean,
    withTooltip?: boolean
}) => {
    if (!listingHasPriceInfo(listing)) {
        return (
            <span
                className="Price Price--noPrice"
                data-wdio="Price"
                data-price="0"
            >
                <FormattedMessage
                    id={'ratesNone'}
                    description={'no price'}
                    defaultMessage={'View details for price'}
                >
                    {(txt: Node): Node => (
                        <span className="Price__Fallback">{txt}</span>
                    )}
                </FormattedMessage>
            </span>
        );
    }
    const {instantBookable} = listing;
    const {nightlyPrice, totalPrice, priceLabel} = getListingPrices(
        listing,
        withTooltip
    );

    const renderPriceItem = (item, key) => {
        const {
            pricePeriodDescription,
            formattedAmount,
            roundedFormattedAmount,
            isPrimary
        } = item;
        const isInsantBookable =
            showInstantBookIcon && instantBookable && isPrimary;
        if (
            !pricePeriodDescription &&
            !formattedAmount &&
            !roundedFormattedAmount
        ) {
            return null;
        }

        const styleClass = classNames('', {
            DualPrice__primary: item.isPrimary,
            DualPrice__secondary: !item.isPrimary,
            'DualPrice__main-price': item.isPriceBold,
            DualPrice__withTooltip: item.withTooltip
        });
        const stylePeriodClass = classNames('', {
            DualPrice__period: true,
            DualPrice__period_amount:
                !roundedFormattedAmount && !formattedAmount
        });

        return (
            <span key={key} className={styleClass}>
                {isInsantBookable && (
                    <span className="DualPrice__instantBook_icon">
                        <InstantBookIcon intl={intl} />
                    </span>
                )}
                <span className="DualPrice__amount">
                    {roundedFormattedAmount || formattedAmount}
                </span>
                <span className={stylePeriodClass}>
                    {pricePeriodDescription}
                </span>
            </span>
        );
    };

    let prices = [nightlyPrice, totalPrice];
    // for mobile view primary item should always be first
    if (!withTooltip && !isDesktop && !prices[0].isPrimary) {
        prices = prices.reverse();
    }

    if (withTooltip && totalPrice.pricePeriodDescription) {
        return isDesktop ? (
            // $FlowFixMe!
            prices.map((price, index) => {
                if (price.withTooltip) {
                    return (
                        <DualPriceTooltipContainer
                            key={`price_${index}`}
                            isDesktop={isDesktop}
                            content={priceLabel.pricePeriodDescription}
                        >
                            {renderPriceItem(price, `price_${index}`)}
                        </DualPriceTooltipContainer>
                    );
                }
                return renderPriceItem(price, `price_${index}`);
            })
        ) : (
            <DualPriceTooltipContainer
                isDesktop={isDesktop}
                content={priceLabel.pricePeriodDescription}
            >
                {prices.map((price, index) =>
                    renderPriceItem(price, `price_${index}`)
                )}
            </DualPriceTooltipContainer>
        );
    }

    return (
        <Fragment>
            {prices.map((price, index) =>
                renderPriceItem(price, `price_${index}`)
            )}
            <span className="DualPrice__priceLabel">
                {priceLabel.pricePeriodDescription}
            </span>
        </Fragment>
    );
};

export default DualPrice;
